import React, { useState, ChangeEvent, KeyboardEvent } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { BsSearch, BsX } from 'react-icons/bs';

import './BaySearchInput.scss';

export interface BaySearchInputProps {
	placeholder?: string;
	onSearch: (value: string, event?: KeyboardEvent<HTMLInputElement> | React.MouseEvent) => void;
	onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
	showClear?: boolean;
	value?: string;
	className?: string;
	inputSize?: 'sm' | 'lg';
	disabled?: boolean;
	helperText?: string;
	inputProps?: Record<string, any>;
}

/**
 * BaySearchInput - A search input component that triggers search on Enter keypress
 *
 * @param {Object} props - Component props
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.onSearch - Function to call when search is triggered (Enter key)
 * @param {Function} props.onChange - Optional function to call on every input change
 * @param {boolean} props.showClear - Whether to show the clear button
 * @param {string} props.value - Controlled value
 * @param {string} props.className - Additional classes
 * @param {string} props.inputSize - Input size (sm, lg)
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.helperText - Helper text shown below the input
 * @param {Object} props.inputProps - Additional props to pass to the Form.Control
 */
const BaySearchInput: React.FC<BaySearchInputProps> = ({
	placeholder = 'Search...',
	onSearch,
	onChange,
	showClear = true,
	value: controlledValue,
	className = '',
	inputSize,
	disabled = false,
	helperText,
	inputProps = {},
}) => {
	const [value, setValue] = useState('');

	// Use either controlled or internal state
	const currentValue = controlledValue !== undefined ? controlledValue : value;

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		if (controlledValue === undefined) {
			setValue(newValue);
		}
		if (onChange) {
			onChange(newValue, e);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onSearch(currentValue, e);
		}
	};

	const handleClear = () => {
		if (controlledValue === undefined) {
			setValue('');
		}
		if (onChange) {
			onChange('');
		}
		if (onSearch) {
			onSearch('');
		}
	};

	const handleSearchClick = () => {
		onSearch(currentValue);
	};

	return (
		<div className={`bay-search-input ${className}`}>
			<InputGroup className="mb-1" size={inputSize}>
				<InputGroup.Text
					className="search-button"
					title="Click to search"
					onClick={!disabled ? handleSearchClick : undefined}
				>
					<BsSearch color="var(--bs-gray-500)" size={16} />
				</InputGroup.Text>
				<Form.Control
					disabled={disabled}
					placeholder={placeholder}
					type="text"
					value={currentValue}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					{...inputProps}
				/>
				{showClear && currentValue && (
					<InputGroup.Text
						className="clear-button"
						style={{ cursor: !disabled ? 'pointer' : 'default' }}
						onClick={!disabled ? handleClear : undefined}
					>
						<BsX size={16} />
					</InputGroup.Text>
				)}
			</InputGroup>
			{helperText && (
				<Form.Text className="text-muted fst-italic">{helperText}</Form.Text>
			)}
		</div>
	);
};

export default BaySearchInput;
